import React, { useEffect, useState, useCallback } from "react";
import { ORIGIN_META, originFromDataMode, OriginSummary } from "../../lib/origin.jsx";
import {
  Cog, RefreshCcw, Zap, Eye, Loader2, Play, CheckCircle2, AlertTriangle, History,
  TrendingUp, Activity, Wallet, ShoppingCart, ArrowRightLeft, Clock3,
} from "lucide-react";
import { Tile, Badge, SectionHeader, StatusDot } from "../../components/Primitives";
import { useRole } from "../../lib/role.jsx";
import {
  enginesOverview, latestCycle, cycleHistory, runNow, runSingle, setEngineConfig,
  ENGINES,
} from "../../lib/enginesApi";
import { formatNumber, cn } from "../../lib/utils";
import FeatureSetPanel from "../../components/classification/FeatureSetPanel";

const ICON = {
  demand_forecast: TrendingUp, stockout_adjusted: Activity, inventory_health: Wallet,
  stockout_risk: AlertTriangle, replenishment: ShoppingCart, redistribution: ArrowRightLeft, aging: Clock3,
};

export default function EnginesAdmin() {
  const { user } = useRole();
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // engineId | "all"
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [ov, hist] = await Promise.all([enginesOverview(), cycleHistory(10)]);
      setOverview(ov);
      setHistory(hist.runs || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onRunAll() {
    setRunning("all");
    try { await runNow(user?.employeeId || "ui"); await load(); }
    catch (e) { setError(e.message); }
    finally { setRunning(null); }
  }
  async function onRunOne(engineId) {
    setRunning(engineId);
    try { await runSingle(engineId, user?.employeeId || "ui"); await load(); }
    catch (e) { setError(e.message); }
    finally { setRunning(null); }
  }
  async function onToggleMode(engineId, nextMode) {
    try { await setEngineConfig(engineId, { mode: nextMode }); await load(); }
    catch (e) { setError(e.message); }
  }

  const engines = overview?.engines || [];
  const latest = overview?.latest_cycle;

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Admin Console · AI Governance"
        title="Inventory Engines Admin"
        description="Operate the seven inventory engines: toggle live vs shadow, trigger cycles, inspect last-run health and cycle history."
        action={
          <div className="flex items-center gap-2">
            {latest?.run_id && (
              <div className="text-[11px] text-muted inline-flex items-center gap-1.5" data-testid="admin-latest-cycle">
                <StatusDot status="success" />
                Last cycle · {(latest.started_at || "").slice(0, 16).replace("T", " ")}
              </div>
            )}
            <button data-testid="admin-reload" onClick={load}
                    className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1.5">
              <RefreshCcw size={12} /> Reload
            </button>
            <button data-testid="admin-run-all" onClick={onRunAll} disabled={running === "all"}
                    className="px-3 py-1.5 text-[11px] bg-primary text-primary-fg hover:opacity-90 rounded-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              {running === "all" ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              {running === "all" ? "Running cycle…" : "Run full cycle"}
            </button>
          </div>
        }
      />

      <OriginSummary variant="system" />

      {error && (
        <div data-testid="admin-error" className="border border-critical/40 bg-critical/5 text-critical rounded-sm p-3 text-[12px]">
          <AlertTriangle size={13} className="inline mr-1" /> {error}
        </div>
      )}

      {/* Engine table */}
      <Tile testid="admin-engines-table" title="Engines · health & operating mode"
            action={<Badge status="info">{engines.length} engines</Badge>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Engine</th>
                <th className="py-2 px-3 text-center">Mode</th>
                <th className="py-2 px-3 text-right">Last Run</th>
                <th className="py-2 px-3 text-right">Duration</th>
                <th className="py-2 px-3 text-right">Recs</th>
                <th className="py-2 px-3 text-right">Expected PHP</th>
                <th className="py-2 px-3 text-left">Data</th>
                <th className="py-2 px-3 text-center">Status</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="py-10 text-center text-muted">
                  <Loader2 size={16} className="inline animate-spin mr-2" /> Loading engines…
                </td></tr>
              )}
              {!loading && engines.map(e => {
                const Icon = ICON[e.id] || Cog;
                const meta = ENGINES.find(x => x.id === e.id) || {};
                const okLast = e.last_status === "ok";
                return (
                  <tr key={e.id} data-testid={`admin-row-${e.id}`}
                      className="border-b border-border hover:bg-surface-2/40 transition-colors">
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <Icon size={13} className="shrink-0 text-primary" />
                        <div>
                          <div className="font-medium">{e.label || meta.label}</div>
                          <div className="text-[10px] text-muted font-mono-tabular">{e.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <ModeToggle mode={e.mode} onToggle={next => onToggleMode(e.id, next)} testid={`admin-mode-${e.id}`} />
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono-tabular text-[11px]" data-testid={`admin-lastrun-${e.id}`}>
                      {e.last_run_at ? e.last_run_at.slice(5, 16).replace("T", " ") : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono-tabular text-[11px]">
                      {e.last_duration_ms != null ? `${e.last_duration_ms} ms` : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono-tabular">
                      {formatNumber(e.last_rec_count || 0)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono-tabular text-[11px]">
                      ₱{formatNumber(e.last_expected_value_php || 0, { compact: true })}
                    </td>
                    <td className="py-2.5 px-3 text-left text-[11px]">
                      <Badge status={e.data_mode === "real_s3_bronze" ? "success" : e.data_mode === "hybrid" ? "info" : "warning"}>
                        {ORIGIN_META[originFromDataMode(e.data_mode)].label}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {e.last_run_at
                        ? <Badge status={okLast ? "success" : "critical"}>{okLast ? <><CheckCircle2 size={10} className="mr-0.5" />ok</> : "failed"}</Badge>
                        : <Badge status="neutral">idle</Badge>}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button data-testid={`admin-run-${e.id}`} onClick={() => onRunOne(e.id)} disabled={running === e.id}
                              className="px-2 py-1 text-[10.5px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                        {running === e.id ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />}
                        Run
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && engines.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-muted">
                  No engines registered. Run the full cycle to initialise.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Tile>

      <FeatureSetPanel />

      {/* Cycle history */}
      <Tile testid="admin-cycle-history" title="Cycle history" action={<History size={12} className="text-muted" />}>
        {history.length === 0 ? (
          <div className="text-center py-6 text-muted text-[12px]">No cycle runs recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                  <th className="py-2 px-3 text-left">Run ID</th>
                  <th className="py-2 px-3 text-left">Started</th>
                  <th className="py-2 px-3 text-right">Engines</th>
                  <th className="py-2 px-3 text-right">Recs</th>
                  <th className="py-2 px-3 text-right">Duration</th>
                  <th className="py-2 px-3 text-left">Actor</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.run_id} data-testid={`cycle-${h.run_id}`}
                      className="border-b border-border hover:bg-surface-2/40 transition-colors">
                    <td className="py-2 px-3 font-mono-tabular text-[10.5px] text-muted">{(h.run_id || "").slice(0, 10)}…</td>
                    <td className="py-2 px-3 font-mono-tabular text-[11px]">
                      {(h.started_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-tabular">
                      {Object.keys(h.engines || {}).length}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-tabular">{h.total_recommendations ?? 0}</td>
                    <td className="py-2 px-3 text-right font-mono-tabular text-[11px]">
                      {h.duration_ms != null ? `${h.duration_ms} ms` : "—"}
                    </td>
                    <td className="py-2 px-3 text-[11px] text-muted">{h.actor || "scheduler"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tile>
    </div>
  );
}

function ModeToggle({ mode, onToggle, testid }) {
  const live = mode === "live";
  return (
    <button data-testid={testid} onClick={() => onToggle(live ? "shadow" : "live")}
            className={cn("inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm text-[10.5px] uppercase tracking-wider font-semibold transition-colors",
              live ? "border-success/40 bg-success/10 text-success"
                   : "border-info/40 bg-info/10 text-info")}>
      {live ? <Zap size={10} /> : <Eye size={10} />}
      {live ? "live" : "shadow"}
    </button>
  );
}
